import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { DefaultIntlFormatValues } from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import { PRODUCT_TYPES } from '../definitions/CurrentDocument';
import FooterDefaultIntlFormatValues from '../definitions/FooterDefaultIntlFormatValues';
import getSummarizedEarnings from './getSummarizedEarnings';
import isEarningsAPercentage from './isEarningsAPercentage';
import isCIBCCreditCard from './isCIBCCreditCard';


/**
 * Get rowTitle and rowSummary pairs for the CreditCardLayout Focus Details section, with optional rowDetails
 *
 * @param {CreditCardSynopsisShape} cardInfo
 * @param {string} productType
 * @return {null|[{
 *              rowTitle: JSX.Element,
 *              rowSummary: JSX.Element,
 *              rowDetails: {key: string, message: JSX.Element},
 *              key: string,
 *         }]}
 */
function getCardLayoutFocusDetailsRows(cardInfo, productType) {
    switch (productType) {
        case PRODUCT_TYPES.LOW_INTEREST:
            return getLowInterestDetails({ cardInfo });

        case PRODUCT_TYPES.BALANCE_TRANSFER:
            return getBalanceTransferDetails({ cardInfo });

        case PRODUCT_TYPES.TRAVEL:
        case PRODUCT_TYPES.CASH_BACK:
        case PRODUCT_TYPES.ANY_REWARDS:
        case PRODUCT_TYPES.STORE_CREDIT:
            return getRewardDetails({ cardInfo, productType });

        case PRODUCT_TYPES.SECURED:
            return getSecuredDetails({ cardInfo });

        default:
            // not just 'unsupported', it would be an illegal product type
            return null;
    }
}

function getLowInterestDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        lowInterestMonthlyBalance,
        lowInterestMonthlyInterestFee,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'pay per month',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.low-interest.pay-per-month.title"
                    defaultMessage="Pay {lowInterestMonthlyInterestFee, number, CAD0}/mo"
                    values={{ lowInterestMonthlyInterestFee }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.low-interest.pay-per-month.summary"
                    defaultMessage="Based on a credit balance of {lowInterestMonthlyBalance, number, CAD0}"
                    values={{ lowInterestMonthlyBalance }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}

function getBalanceTransferDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        balanceTransferMonthlyPayment,
        balanceTransferPromoMonths,
        balanceTransferAmount,
        balanceTransferFeeRate,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'pay per month',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.pay-per-month.title"
                    defaultMessage="Pay {balanceTransferMonthlyPayment, number, CAD2}/mo"
                    values={{ balanceTransferMonthlyPayment }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.pay-per-month.summary"
                    defaultMessage={`during the promo to pay off
                        {balanceTransferAmount, number, CAD0} transferred balance`}
                    values={{
                        balanceTransferPromoMonths,
                        balanceTransferAmount,
                    }}
                />
            ),
        },
        {
            key: 'balance transfer fee',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.balance-transfer-fee.title"
                    defaultMessage="{balanceTransferFeeRate, number, percent} Balance transfer fee"
                    values={{ balanceTransferFeeRate: balanceTransferFeeRate / 100 }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.balance-transfer-fee.summary"
                    defaultMessage="A fee of {dollarValue, number, CAD0} will be added to your balance"
                    values={{ dollarValue: (balanceTransferFeeRate / 100) * balanceTransferAmount }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}

function getRewardDetails({ cardInfo, productType }) {
    const {
        id: cardId,
        productType: cardProductType,
        annualFee,
        isFirstYearFeeWaived,
        earningsDescription,
        welcomeBonusDescription,
        signUpBonusPoints,
        signUpBonusDollars,
        anniversaryBonusDescription,
        anniversaryBonus,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    const isEarningsShownAsPercentage = isEarningsAPercentage({ productType, cardId, cardProductType });
    const earningsSummary = getSummarizedEarnings(isEarningsShownAsPercentage, earningsDescription);

    // always start with earning rewards
    const rows = [
        {
            key: 'earn rewards',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.earn-rewards.title"
                    defaultMessage="Earn rewards"
                />
            ),
            rowSummary: earningsSummary.summaryMessage,
            rowDetails: earningsSummary.summaryDetails,
        },
    ];

    // add Welcome bonus info if we have sufficient data
    if (welcomeBonusDescription && signUpBonusDollars && (signUpBonusPoints || isEarningsShownAsPercentage)) {
        rows.push({
            key: 'welcome bonus',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.welcome-bonus.title"
                    defaultMessage="Welcome bonus"
                />
            ),
            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
            rowSummary: isEarningsShownAsPercentage
                ? (
                    <FormattedNumber
                        style="currency"
                        currency="CAD"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                        value={signUpBonusDollars}
                    />
                )
                : (
                    <FormattedMessage
                        id="edb.credit-card.focus-details.welcome-bonus.points"
                        defaultMessage="{signUpBonusPoints, number} points (a {signUpBonusDollars, number, CAD0} value)"
                        values={{
                            signUpBonusPoints,
                            signUpBonusDollars,
                        }}
                    />
                ),
            rowDetails: [ {
                key: welcomeBonusDescription,
                message: welcomeBonusDescription,
            } ],
        });
    }

    // add Anniversary bonus info if we have sufficient data
    if (anniversaryBonusDescription && anniversaryBonus?.value && (anniversaryBonus?.points || isEarningsShownAsPercentage)) {
        rows.push({
            key: 'anniversary bonus',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.anniversary-bonus.title"
                    defaultMessage="Anniversary bonus"
                />
            ),
            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
            rowSummary: isEarningsShownAsPercentage
                ? (
                    <FormattedNumber
                        style="currency"
                        currency="CAD"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                        value={anniversaryBonus.value}
                    />
                )
                : (
                    <FormattedMessage
                        id="edb.credit-card.anniversary-bonus.value"
                        defaultMessage="{anniversaryBonusPoints, number} points (a {anniversaryBonusValue, number, CAD0} value)"
                        values={{
                            ...DefaultIntlFormatValues,
                            anniversaryBonusPoints: anniversaryBonus.points,
                            anniversaryBonusValue: anniversaryBonus.value,
                        }}
                    />
                ),
            rowDetails: [ {
                key: anniversaryBonusDescription,
                message: anniversaryBonusDescription,
            } ],
        });
    }

    rows.push(getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }));

    return rows;
}

function getSecuredDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        securedMonthlyBalance,
        securedMonthlyInterestFee,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'monthly interest',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.secured.pay-per-month.title"
                    defaultMessage="Pay {securedMonthlyInterestFee, number, CAD2}/mo in interest"
                    values={{ securedMonthlyInterestFee }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.secured.pay-per-month.summary"
                    defaultMessage="Based on a credit balance of {securedMonthlyBalance, number, CAD0}"
                    values={{ securedMonthlyBalance }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}


function getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }) {
    return {
        key: 'annual fee',
        rowTitle: (
            <FormattedMessage
                id="edb.credit-card.annual-fee"
                defaultMessage="Annual fee"
            />
        ),
        // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
        rowSummary: isCIBC
            ? (
                <FormattedMessage
                    id="edb.credit-card.annual-fee.cibc.summary"
                    defaultMessage="see Details"
                />
            )
            : (
                <FormattedMessage
                    {...MESSAGES.annualFeeSummary}
                    values={{
                        annualFee,
                        isFirstYearFeeWaived,
                        zero: 0,
                        ...FooterDefaultIntlFormatValues,
                        ...DefaultIntlFormatValues,
                    }}
                />
            ),
    };
}


export default getCardLayoutFocusDetailsRows;
